type ImageSource = {
  url?: string;
};

export type CriticalImagePreload = {
  href: string;
  media?: string;
};

const toPreload = (
  href?: string,
  media?: string,
): CriticalImagePreload | null => {
  if (!href) {
    return null;
  }

  return media ? { href, media } : { href };
};

type PageComponent = {
  componentType?: string;
  componentProps?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getImageSource = (value: unknown): ImageSource => {
  if (!isRecord(value) || typeof value.url !== 'string') {
    return {};
  }

  return { url: value.url };
};

const getPageComponents = (content: unknown): PageComponent[] => {
  if (!Array.isArray(content) || content.length === 0) {
    return [];
  }

  const [page] = content;
  if (!isRecord(page) || !Array.isArray(page.components)) {
    return [];
  }

  return page.components.filter(isRecord).map(component => ({
    componentType:
      typeof component.componentType === 'string'
        ? component.componentType
        : undefined,
    componentProps: component.componentProps,
  }));
};

const getHeroPreloads = (imageSource?: unknown): CriticalImagePreload[] => {
  if (!isRecord(imageSource)) {
    return [];
  }

  return [
    toPreload(
      getImageSource(imageSource.mobileImage).url,
      '(max-width: 767px)',
    ),
    toPreload(
      getImageSource(imageSource.desktopImage).url,
      '(min-width: 768px)',
    ),
  ].filter((item): item is CriticalImagePreload => Boolean(item));
};

const getHeroComponentPreloads = (
  component: PageComponent,
): CriticalImagePreload[] | null => {
  const componentProps = component.componentProps;

  switch (component.componentType) {
    case 'slideshow-banner': {
      const slides = isRecord(componentProps) ? componentProps.slides : [];
      const firstSlide = Array.isArray(slides) ? slides[0] : undefined;
      return getHeroPreloads(firstSlide);
    }
    case 'herobanner':
    case 'tailfin-hero-banner-two': {
      return getHeroPreloads(componentProps);
    }
    case 'tailfin-hero-banner': {
      const imageUrl = isRecord(componentProps)
        ? getImageSource(componentProps.image).url
        : undefined;
      const image = toPreload(imageUrl);
      return image ? [image] : [];
    }
    default:
      return null;
  }
};

const getImageComponentPreloads = (
  component: PageComponent,
): CriticalImagePreload[] => {
  if (component.componentType !== 'image') {
    return [];
  }

  const imageUrl = getImageSource(component.componentProps).url;
  const image = toPreload(imageUrl);
  return image ? [image] : [];
};

export const getCriticalImagePreloads = (
  content: unknown,
): CriticalImagePreload[] => {
  const components = getPageComponents(content);

  for (const component of components) {
    const heroPreloads = getHeroComponentPreloads(component);

    if (heroPreloads && heroPreloads.length > 0) {
      return heroPreloads;
    }
  }

  for (const component of components) {
    const imagePreloads = getImageComponentPreloads(component);

    if (imagePreloads.length > 0) {
      return imagePreloads;
    }
  }

  return [];
};
